

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { getSpecification, saveEvaluation, getSupplierNames } from '../../../models/specificationModel';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { safeParseDate, formatDateForDisplay } from '../../../utils/dateUtils';
import { useAuth } from '../../../context/AuthContext';
import QuoteModal from '../../../components/modals/QuoteModal';
import QuoteViewModal from '../../../components/modals/QuoteViewModal';
import AttachQuotationModal from '../../../components/modals/AttachQuotationModal';
import SpecificationFilesModal from './components/SpecificationFilesModal';
import Button from '../../../components/common/Button';
import { parseFinancialValue } from '../../../utils/numberUtils';
import { ensureStringId, compareIds } from '../../../utils/idUtils';
import '../../../styles/colors.css';

// Utility function to safely parse and format dates
const formatDate = (dateValue) => {
  if (!dateValue) return 'Not specified';
  
  const date = safeParseDate(dateValue);
  if (!date) {
    return 'Invalid Date';
  }
  
  return formatDateForDisplay(date);
};

function ViewSpecification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { specificationId } = useParams();
  const { user, can, originalUser } = useAuth();
  const [specification, setSpecification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showQuoteViewModal, setShowQuoteViewModal] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [parsedSuppliers, setParsedSuppliers] = useState([]);
  const [details, setDetails] = useState(null);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [showAttachQuotationModal, setShowAttachQuotationModal] = useState(false);
  const [selectedSupplierForQuote, setSelectedSupplierForQuote] = useState(null);
  const [existingQuoteForEdit, setExistingQuoteForEdit] = useState(null); // For editing existing quotes
  
  // Attachments state
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  
  // Use a ref to track if we've navigated away from the app
  const hasLeftApp = useRef(false);

  // Fetch attachments for this specification
  const fetchAttachments = useCallback(async () => {
    if (!specificationId) return;
    
    try {
      const response = await api.get(`/api/attachments/specifications/${specificationId}/list?type=specification`);
      
      // Handle nested response structure: response.data.data.attachments
      const attachments = response.data?.data?.attachments || response.data?.attachments || [];
      setAttachments(attachments);
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  }, [specificationId]);

  // Handle attachment updates
  const handleFilesUpdated = useCallback(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  // Parse suppliers and enrich with submission data
  const parseSuppliers = (suppliersData, submissions = []) => {
    // Handle different data formats from API
    if (!suppliersData) return [];
    
    let supplierCodes = [];
    if (typeof suppliersData === 'object' && Array.isArray(suppliersData)) {
      supplierCodes = suppliersData;
    } else {
      try {
        supplierCodes = JSON.parse(suppliersData);
      } catch (error) {
        console.warn('Error parsing suppliers:', error);
        return [];
      }
    }
    
    // Clean and process supplier data to handle malformed JSON
    const cleanedSuppliers = [];
    let currentObject = '';
    
    for (let i = 0; i < supplierCodes.length; i++) {
      const item = supplierCodes[i];
      
      // If it's a simple supplier code (numeric or YYMMDD-XXXX format), add it directly
      if (typeof item === 'string' && /^[\d-]+$/.test(item.trim()) && item.trim().length > 0) {
        cleanedSuppliers.push(item.trim());
        continue;
      }
      
      // If it's a numeric value, convert to string
      if (typeof item === 'number') {
        cleanedSuppliers.push(item.toString());
        continue;
      }
      
      // Handle JSON object fragments (malformed JSON case)
      if (typeof item === 'string') {
        if (item.trim().startsWith('{')) {
          currentObject = item;
          // Look ahead to collect all parts of the broken JSON
          let j = i + 1;
          while (j < supplierCodes.length && !supplierCodes[j].includes('}')) {
            currentObject += ',' + supplierCodes[j];
            j++;
          }
          // Add the closing part
          if (j < supplierCodes.length) {
            currentObject += ',' + supplierCodes[j];
          }
          
          try {
            // Fix common JSON issues and parse
            let fixedJson = currentObject
              .replace(/,\s*}/g, '}') // Remove trailing commas
              .replace(/(\w+):/g, '"$1":') // Quote property names
              .replace(/:\s*(\w+)/g, ': "$1"'); // Quote property values if not quoted
            
            const parsed = JSON.parse(fixedJson);
            if (parsed.BP_Code) {
              cleanedSuppliers.push(parsed.BP_Code.toString());
            }
          } catch (parseError) {
            console.warn('Could not parse malformed JSON supplier:', currentObject, parseError);
          }
          
          i = j; // Skip the processed items
          currentObject = '';
        } else if (!item.includes('"BP_Code"') && !item.includes('"BP_Name"')) {
          // If it's just a regular string that might be a supplier code
          const cleaned = item.replace(/[^0-9-]/g, '').replace(/^-+|-+$/g, '');
          if (cleaned && cleaned.length > 0) {
            cleanedSuppliers.push(cleaned);
          }
        }
      }
    }
    
    // Map supplier codes to supplier objects with names from submissions
    const parsedSuppliers = cleanedSuppliers.map(code => {
      const submission = submissions.find(s => String(s.quotation_company_code) === String(code));
      return {
        company_code: code,
        company_name: submission ? submission.quotation_company_name : null
      };
    });
    
    return parsedSuppliers;
  };

  // Function to enrich suppliers with names from API if not available
  const enrichSuppliersWithNames = async (suppliers) => {
    const suppliersNeedingNames = suppliers.filter(s => !s.company_name);
    
    if (suppliersNeedingNames.length === 0) {
      return suppliers.map(s => ({
        ...s,
        company_name: s.company_name || `Supplier ${s.company_code}`
      }));
    }
    
    try {
      // Use the existing getSupplierNames function
      const supplierCodes = suppliersNeedingNames.map(s => s.company_code);
      const supplierNames = await getSupplierNames(supplierCodes);
      
      return suppliers.map(supplier => {
        if (!supplier.company_name) {
          const foundName = supplierNames[supplier.company_code];
          return {
            ...supplier,
            company_name: foundName || `Unknown Supplier (${supplier.company_code})`
          };
        }
        return supplier;
      });
    } catch (error) {
      console.warn('⚠️ Failed to fetch supplier names:', error);
      return suppliers.map(s => ({
        ...s,
        company_name: s.company_name || `Unknown Supplier (${s.company_code})`
      }));
    }
  };

  // Helper function to build the timeline with all events
  const buildTimeline = (data) => {
    const spec = data.specification;
    const timeline = [];
    
    // Check if this is an ESR (Emergency and Service Rendered) specification
    const isESR = spec.specification_emergency === 1 || spec.specification_emergency === '1';

    if (isESR) {
      // ESR WORKFLOW - Simplified timeline for Emergency and Service Rendered
      // Always show all steps in fixed order, with appropriate status
      
      // Step 1: Specification Created (always completed)
      timeline.push({
        id: 'created',
        title: 'Specification Created',
        date: safeParseDate(spec.specification_date) || new Date(),
        status: 'completed',
        icon: 'document'
      });
      
      // Step 2: Supplier Selection (always show)
      // Check if spec has supplier data - should be an array with length > 0
      const hasSupplierData = Array.isArray(spec.specification_supplier) && spec.specification_supplier.length > 0;
      timeline.push({
        id: 'suppliers_selected',
        title: hasSupplierData ? 
          `${spec.specification_supplier.length} Supplier${spec.specification_supplier.length > 1 ? 's' : ''} Selected` : 
          'Supplier Selection',
        date: hasSupplierData ? safeParseDate(spec.specification_date) || new Date() : null,
        status: hasSupplierData ? 'completed' : 'pending',
        icon: 'users'
      });
        


      // Step 4: Quotation (always show) - ESR can now have multiple quotations
      const hasQuotations = data.submissions && data.submissions.length > 0;
      const quotationCount = hasQuotations ? data.submissions.length : 0;
      const isESR = spec.specification_emergency === 1 || spec.specification_emergency === '1';
      
      timeline.push({
        id: hasQuotations ? 'quotes_received' : 'attach_quotation',
        title: hasQuotations ? `Quotation${quotationCount > 1 ? 's' : ''} Received (${quotationCount})` : 'Attach Quotation',
        date: hasQuotations ? 
          safeParseDate(data.submissions[0].quotation_date || data.submissions[0].quotation_time) || new Date() : null,
        status: hasQuotations ? 'completed' : 'pending',
        icon: 'document',
        // Only allow attaching quotation if we have supplier data AND it's an ESR specification
        action: hasQuotations ? null : (hasSupplierData && isESR ? 'attach_quotation' : null)
      });
      
      // Step 5: Evaluation (show only if quotations exist)
      if (hasQuotations) {
        const evaluatedQuotes = data.submissions.filter(q => q.quotation_evaluation && q.quotation_evaluation.trim().length > 4);
        const allEvaluated = evaluatedQuotes.length === quotationCount;
        
        timeline.push({
          id: 'evaluation',
          title: quotationCount > 1 ? 
            `Evaluation (${evaluatedQuotes.length}/${quotationCount})` : 
            'Evaluation',
          date: allEvaluated && evaluatedQuotes.length > 0 ? new Date() : null,
          status: allEvaluated ? 'completed' : (evaluatedQuotes.length > 0 ? 'in_progress' : 'pending'),
          icon: allEvaluated ? 'check-circle' : 'clipboard'
        });
      }
      
      // Step 6: Requisition Creation (always show)
      const hasRequisition = spec.requisition_number != null && spec.requisition_number !== '' && spec.requisition_number !== 'null';
      timeline.push({
        id: hasRequisition ? 'requisition' : 'requisition_pending',
        title: hasRequisition ? 'Requisition Created' : 'Requisition Pending',
        date: hasRequisition ? safeParseDate(spec.requisition_date) || new Date() : null,
        status: hasRequisition ? 'completed' : 'pending',
        icon: hasRequisition ? 'document' : 'time',
        action: null, // Remove action for both completed and pending requisitions
        requisition_id: hasRequisition ? (spec.requisition_id || spec.requisition_number) : null
      });
      
      // Step 6: Requisition Approval (always show)
      const isApproved = spec.requisition_status && spec.requisition_status > 0;
      timeline.push({
        id: isApproved ? 'approval' : 'approval_pending',
        title: 'Requisition Approval',
        date: isApproved ? (safeParseDate(spec.requisition_date) || new Date()) : null,
        status: isApproved ? 'completed' : 'pending',
        icon: isApproved ? 'check' : 'time'
      });
    } else {
      // STANDARD RFQ WORKFLOW
      
      // Step 1: Specification Created
      timeline.push({
        id: 'created',
        title: 'Specification Created',
        date: safeParseDate(spec.specification_date) || new Date(),
        status: 'completed',
        icon: 'document'
      });

      // Step 2: Suppliers Selected
      const suppliers = parseSuppliers(spec.specification_supplier);
      
      const hasSuppliers = suppliers && suppliers.length > 0;
      timeline.push({
        id: 'suppliers',
        title: hasSuppliers ? 
          `${suppliers.length} Supplier${suppliers.length > 1 ? 's' : ''} Selected` : 
          'Suppliers Selected',
        date: hasSuppliers ? (safeParseDate(spec.specification_date) || new Date()) : null,
        status: hasSuppliers ? 'completed' : 'pending',
        icon: 'users'
      });
      

      
      // Declare isSent early since we need it for ready for sending logic
      const isSent = spec.procurement_status > 0;
      
      // Step 4: Ready for Sending (procurement_send == 1 OR already sent)
      // If it's already sent, it must have been ready for sending at some point
      const isReadyForSending = spec.procurement_send == 1 || isSent;
      
      // Permission checks for marking as ready for sending
      const isCreator = spec.specification_user === user?.user_id;
      const isSameDepartment = spec.department === user?.department;
      const canMarkBasedOnCreator = isCreator || isSameDepartment;
      
      const canMarkReadyForSending = !isReadyForSending && !isSent && canMarkBasedOnCreator;
      timeline.push({
        id: isReadyForSending ? 'ready_for_sending' : 'mark_ready_for_sending',
        title: isReadyForSending ? 'Ready for Sending' : 'Ready for sending',
        date: isReadyForSending ? new Date() : null,
        status: isReadyForSending ? 'completed' : 'pending',
        icon: isReadyForSending ? 'check-circle' : 'time',
        action: canMarkReadyForSending ? 'mark_ready_for_sending' : null
      });
      
      // Step 5: Sent (procurement_status == 2)
      timeline.push({
        id: 'sent',
        title: 'Sent',
        date: isSent ? (safeParseDate(spec.procurement_time) || new Date()) : null,
        status: isSent ? 'completed' : 'pending',
        icon: isSent ? 'mail' : 'time'
      });
      
      // Step 6: Quotations (data.submissions.length > 0)
      const hasQuotations = data.submissions && data.submissions.length > 0;
      const quotationCount = hasQuotations ? data.submissions.length : 0;
      const isESR = spec.specification_emergency === 1 || spec.specification_emergency === '1';
      timeline.push({
        id: hasQuotations ? 'quotations_received' : 'quotations_pending',
        title: hasQuotations ? 
          `Quotations (${quotationCount})` : 
          'Quotations',
        date: hasQuotations ? 
          (safeParseDate(data.submissions[0].quotation_date || data.submissions[0].quotation_time) || new Date()) : null,
        status: hasQuotations ? 'completed' : 'pending',
        icon: hasQuotations ? 'chart' : 'time',
        // Only show view_quotes action for ESR specifications
        action: hasQuotations && isESR ? 'view_quotes' : null
      });
      
      // Step 7: Closed (procurement_closed == 1)
      const isClosed = spec.procurement_closed == 1;
      timeline.push({
        id: 'closed',
        title: 'Closed',
        date: isClosed ? new Date() : null,
        status: isClosed ? 'completed' : 'pending',
        icon: isClosed ? 'check' : 'time'
      });
      
      // Step 8: Evaluation (show only if quotations exist)
      if (hasQuotations) {
        const evaluatedQuotes = data.submissions.filter(q => q.quotation_evaluation && q.quotation_evaluation.trim().length > 4);
        const allEvaluated = evaluatedQuotes.length === quotationCount;
        
        timeline.push({
          id: 'evaluation',
          title: quotationCount > 1 ? 
            `Evaluation (${evaluatedQuotes.length}/${quotationCount})` : 
            'Evaluation',
          date: allEvaluated && evaluatedQuotes.length > 0 ? new Date() : null,
          status: allEvaluated ? 'completed' : (evaluatedQuotes.length > 0 ? 'in_progress' : 'pending'),
          icon: allEvaluated ? 'check-circle' : 'clipboard'
        });
      }

      // Step 9: Requisition Created
      const hasRequisition = spec.requisition_number && spec.requisition_number !== '' && spec.requisition_number !== 'null';
      timeline.push({
        id: hasRequisition ? 'requisition' : 'requisition_pending',
        title: 'Requisition Created',
        date: hasRequisition ? (safeParseDate(spec.requisition_date) || new Date()) : null,
        status: hasRequisition ? 'completed' : 'pending',
        icon: hasRequisition ? 'document' : 'time',
        action: null,
        requisition_id: hasRequisition ? (spec.requisition_id || spec.requisition_number) : null
      });
    }
    
    return timeline;
  };
  
  // Get the current step action based on role and specification state
  const getCurrentStepAction = () => {
    if (!user || !specification) return null;
    
    const isCreator = user && specification.specification_user === user.user_id;
    const hasSuppliers = parsedSuppliers && parsedSuppliers.length > 0;
    const isESR = specification.specification_emergency === 1 || specification.specification_emergency === '1';
    
    // For Creators: Add suppliers (if no suppliers yet)
    if (isCreator && !hasSuppliers) {
      return {
        title: 'Add Suppliers',
        description: 'Select suppliers for this specification',
        action: () => {
          navigate(`/dashboard/specifications/${specification.specification_id}/edit`);
        },
        buttonText: 'Add Suppliers'
      };
    }
    
 
    // For Creators with RFQ: Create RFQ (if has suppliers but no procurement created)
    if (isCreator && !isESR && hasSuppliers && !specification.procurement_id) {
      return {
        title: 'Create RFQ',
        description: 'Create Request for Quotation to send to suppliers',
        action: () => {
          console.log('Create RFQ for specification:', specification.specification_id);
        },
        buttonText: 'Create RFQ'
      };
    }
    
    return null;
  };

  // Helper function to check if user can see quotation button (legacy)
  const canShowQuotationButton = () => {
    if (!user || !specification) return false;
    
    const isCreator = user && specification.specification_user === user.user_id;
    const hasSuppliers = parsedSuppliers && parsedSuppliers.length > 0;
    const isESR = specification.specification_emergency === 1 || specification.specification_emergency === '1';
    
    // RFQ types (non-ESR) should NOT have quote controls - they only accept supplier submissions
    if (!isESR) {
      console.log('Cannot show quotation button: RFQ types only accept supplier submissions');
      return false;
    }
    
    // For ESR (Emergency) - original logic: specification_emergency == 1 && specification_user == user_id() && suppliers > 0 && submissions == 0
    if (isESR) {
      return isCreator && hasSuppliers && quotes.length === 0;
    }
    
    return false;
  };

  // Helper function to check if user can see "Create RFQ" button
  const canShowCreateRFQButton = () => {
    // Match PHP logic: specification_emergency == 0 && specification_user == user_id() && suppliers > 0 && procurement_id == null
    if (!user || !specification) return false;
    
    const isCreator = user && specification.specification_user === user.user_id;
    const hasSuppliers = parsedSuppliers && parsedSuppliers.length > 0;
    const isRegularRFQ = specification.specification_emergency === 0 || specification.specification_emergency === '0';
    const noProcurementCreated = !specification.procurement_id;
    
    return isRegularRFQ && isCreator && hasSuppliers && noProcurementCreated;
  };

  const fetchSpecification = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch specification data from API
      const data = await getSpecification(specificationId);
      
      if (!data || !data.specification) {
        throw new Error('Invalid response from server');
      }
      
      setSpecification(data.specification);
      
      // Set quotes if available
      if (data.submissions && Array.isArray(data.submissions)) {
        console.log('🔍 API RESPONSE - Submissions data:', data.submissions);
        
        // Log detailed information about each quote ID for debugging
        if (data.submissions.length > 0) {
          console.log('🔍 API RESPONSE - Quote IDs found:');
          data.submissions.forEach(quote => {
            console.log(`   Quote ID: ${quote.quotation_id} (${typeof quote.quotation_id})`);
            console.log(`   Company: ${quote.quotation_company_name}`);
            console.log(`   Company Code: ${quote.quotation_company_code} (${typeof quote.quotation_company_code})`);
          });
        } else {
          console.log('🔍 API RESPONSE - No quotations found');
        }
        
        setQuotes(data.submissions);
      } else {
        console.log('🔍 API RESPONSE - No submissions data available');
        setQuotes([]);
      }
      
      // For ESR specifications, details are now handled through submissions array
      if (data.details && (data.specification.specification_emergency !== 1 && data.specification.specification_emergency !== '1')) {
        // Only use details for non-ESR specifications (regular RFQ flow)
        console.log('📋 Setting details for regular specification:', {
          quotation_id: data.details.quotation_id,
          quotation_status: data.details.quotation_status,
          quotation_company_name: data.details.quotation_company_name
        });
        setDetails(data.details);
      } else {
        console.log('ℹ️ ESR specification - using submissions array instead of details');
        setDetails(null);
      }
      
      // Parse suppliers data with submission names
      const suppliers = parseSuppliers(data.specification.specification_supplier, data.submissions);
      const enrichedSuppliers = await enrichSuppliersWithNames(suppliers);
      setParsedSuppliers(enrichedSuppliers);
      
      // Build timeline with all available data
      setTimeline(buildTimeline(data));
      setError(null);
    } catch (err) {
      setError('Failed to load specification: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [specificationId]);

  // Initial data fetch when component mounts or ID changes
  useEffect(() => {
    fetchSpecification();
    fetchAttachments();
  }, [fetchSpecification, fetchAttachments, location.state?.refreshData]);
  
  // Rebuild timeline when suppliers are loaded or role changes
  useEffect(() => {
    if (specification && parsedSuppliers.length > 0) {
      const data = { specification, submissions: quotes, details };
      setTimeline(buildTimeline(data));
    }
  }, [parsedSuppliers, specification, quotes, details]);

  // Setup page visibility tracking but don't reload automatically
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is now hidden (user switched tabs/apps)
        hasLeftApp.current = true;
      } else if (hasLeftApp.current) {
        // Page is visible again AND we were previously hidden
        // Just reset the flag, but don't reload data automatically
        hasLeftApp.current = false;
        
        // We no longer auto-reload on focus, the user can
        // manually refresh if needed
      }
    };

    // Add event listener for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Clean up on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchSpecification, showAttachQuotationModal, showQuoteModal, showDebugModal]);

  // Handle marking specification as ready for sending
  const handleMarkReadyForSending = async () => {
    // Show SweetAlert2 confirmation dialog
    const result = await Swal.fire({
      title: 'Mark as Ready for Sending?',
      text: 'This will allow buyers to send the specification to suppliers. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, mark as ready',
      cancelButtonText: 'Cancel'
    });
    
    if (!result.isConfirmed) {
      return; // User cancelled
    }

    try {
      setLoading(true);
      
      const response = await api.put(`/api/specifications/${specificationId}/mark-ready-for-sending`);
      
      if (response.data && response.data.status === 1) {
        await Swal.fire({
          title: 'Success!',
          text: 'Specification has been marked as ready for sending.',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745'
        });
        // Refresh the specification data to update the timeline
        await fetchSpecification();
      } else {
        await Swal.fire({
          title: 'Error!',
          text: 'Failed to mark specification as ready for sending.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('Error marking specification as ready for sending:', error);
      await Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to mark specification as ready for sending',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle marking specification for deletion
  const handleMarkForDeletion = async () => {
    // Check if RFQ is already published (procurement_status = 1)
    const isPublished = specification.procurement_status === 1;
    
    // First show warning if published
    if (isPublished) {
      const warningResult = await Swal.fire({
        title: 'RFQ Already Published!',
        html: `
          <div class="text-left">
            <p class="mb-3 text-red-600 font-semibold">⚠️ This RFQ has already been sent to suppliers.</p>
            <p class="mb-2">Marking this specification for deletion will:</p>
            <ul class="list-disc list-inside mb-3 text-sm text-gray-700">
              <li>NOT cancel the RFQ that was sent to suppliers</li>
              <li>Suppliers will still be able to submit quotations</li>
              <li>The RFQ will remain active until the closing date</li>
            </ul>
            <p class="text-sm font-medium">Are you sure you want to proceed?</p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, I understand',
        cancelButtonText: 'Cancel'
      });
      
      if (!warningResult.isConfirmed) {
        return; // User cancelled after seeing warning
      }
    }
    
    // Show SweetAlert2 input dialog to get deletion reason
    const result = await Swal.fire({
      title: 'Mark for Deletion?',
      text: 'Please provide a reason for marking this specification for deletion.',
      input: 'text',
      inputPlaceholder: 'Enter reason for deletion...',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, mark for deletion',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value || value.trim() === '') {
          return 'Please provide a reason for deletion';
        }
      }
    });
    
    if (!result.isConfirmed) {
      return; // User cancelled
    }

    try {
      setLoading(true);
      
      const response = await api.put(`/api/specifications/${specificationId}/mark-for-deletion`, {
        comment: result.value
      });
      
      if (response.data && response.data.status === 1) {
        await Swal.fire({
          title: 'Success!',
          text: 'Specification has been marked for deletion.',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745'
        });
        // Refresh the specification data
        await fetchSpecification();
      } else {
        await Swal.fire({
          title: 'Error!',
          text: response.data?.message || 'Failed to mark specification for deletion.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('Error marking specification for deletion:', error);
      await Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to mark specification for deletion',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle various actions based on timeline
  const handleTimelineAction = (action, data) => {
    switch (action) {
      case 'attach_quotation':
        // Check if this is an ESR specification first
        const isESR = specification?.specification_emergency === 1 || specification?.specification_emergency === '1';
        if (!isESR) {
          toast.warn('RFQ specifications only accept quotes from suppliers');
          break;
        }
        setShowAttachQuotationModal(true);
        break;
      case 'mark_ready_for_sending':
        handleMarkReadyForSending();
        break;
      case 'view_quotes':
        // Navigate to quotes list or open modal
        break;
      case 'view_requisition':
        if (data && data.requisition_id && data.requisition_id !== '' && data.requisition_id !== 'null') {
          navigate(`/dashboard/requisitions/${data.requisition_id}`);
        } else {
          console.warn('Invalid requisition ID:', data?.requisition_id);
        }
        break;
      case 'view_amendment':
        if (data && data.requisition_id && data.requisition_id !== '' && data.requisition_id !== 'null') {
          navigate(`/dashboard/requisitions/${data.requisition_id}?view=amendment`);
        } else {
          console.warn('Invalid requisition ID for amendment:', data?.requisition_id);
        }
        break;
      case 'view_purchase_order':
        if (data && data.purchase_order_id) {
          // Navigate to purchase order page
          navigate(`/dashboard/purchase-orders/${data.purchase_order_id}`);
        }
        break;
      default:
        break;
    }
  };

  // Toggle the debug modal
  const toggleDebugModal = () => {
    setShowDebugModal(!showDebugModal);
  };

  // Open the quote modal for a specific quote
  const handleQuoteView = (companyCode) => {
    console.log('📋 Opening quote view for company code:', companyCode);
    
    // Try direct exact match first without any conversions
    let quote = quotes.find(q => q.quotation_company_code === companyCode);
    
    // If no direct match, try string comparison
    if (!quote) {
      console.log('⚠️ No direct match with original company code, trying string comparison...');
      const stringCompanyCode = ensureStringId(companyCode);
      quote = quotes.find(q => ensureStringId(q.quotation_company_code) === stringCompanyCode);
    }
    
    // If still no match, try looser matching as a last resort
    if (!quote && quotes.length > 0) {
      console.warn('⚠️ No exact company code match found. Trying alternative matching...');
      
      const stringCompanyCode = ensureStringId(companyCode);
      // Try partial matching (in case of prefix/suffix differences)
      quote = quotes.find(q => {
        return ensureStringId(q.quotation_company_code).includes(stringCompanyCode) || 
               stringCompanyCode.includes(ensureStringId(q.quotation_company_code));
      });
      
      // If still no match and we only have one quote, use that as a fallback
      if (!quote && quotes.length === 1) {
        console.warn('⚠️ Using the only available quote as fallback');
        quote = quotes[0];
      }
    }
    
    if (quote) {
      console.log('✅ Found quote:', quote);
      console.log('   Original Quote ID from API:', quote.quotation_id);
      console.log('   Company:', quote.quotation_company_name);
      console.log('   Original Company Code from API:', quote.quotation_company_code);
      setSelectedQuote(quote);
      setShowQuoteModal(true);
    } else {
      console.error('❌ No quote found for company code:', companyCode);
      console.error('   Available quotes:', quotes.map(q => ({
        id: q.quotation_id,
        company: q.quotation_company_name,
        code: q.quotation_company_code
      })));
      toast.error('Quote not found - Please refresh and try again');
    }
  };

  // Open the evaluation modal for a specific quote
  const handleQuoteEvaluate = (quotationId) => {
    console.log('🔥 EVALUATE BUTTON CLICKED - Quote ID:', quotationId);
    
    // Convert IDs to strings to ensure exact comparison
    const stringQuotationId = ensureStringId(quotationId);
    console.log('🔍 Looking for quotation with ID (string):', stringQuotationId);
    
    // Get the exact list of IDs from our data for comparison
    const availableIds = quotes.map(q => q.quotation_id);
    console.log('🔍 Available quote IDs exactly as received from API:', availableIds);
    
    // Try exact match first using exact data from the API
    let quote = quotes.find(q => q.quotation_id === quotationId);
    
    // If no direct match, try string comparison
    if (!quote) {
      console.log('⚠️ No direct match with original ID, trying string comparison...');
      quote = quotes.find(q => ensureStringId(q.quotation_id) === stringQuotationId);
    }
    
    // If still no match, try looser matching as a last resort
    if (!quote) {
      console.warn('⚠️ No exact quote ID match found. Trying to match by company code...');
      // Get the company code from the supplier list
      const supplierCode = parsedSuppliers.find(s => {
        // The ID might be related to the company code
        return ensureStringId(s.company_code).includes(stringQuotationId.substring(0, 10)) || 
               stringQuotationId.includes(ensureStringId(s.company_code));
      })?.company_code;
      
      if (supplierCode) {
        console.log('🔍 Found potential supplier code match:', supplierCode);
        quote = quotes.find(q => compareIds(q.quotation_company_code, supplierCode));
      }
    }
    
    if (quote) {
      console.log('✅ Found quote:', quote);
      console.log('   Original Quote ID from API:', quote.quotation_id);
      setSelectedQuote(quote);
      setShowQuoteViewModal(true);
    } else {
      console.error('❌ No quote found for quotation ID:', quotationId);
      console.error('   Available quote IDs (exactly as in API):', availableIds);
      toast.error('Quote not found - Please refresh and try again');
    }
  };

  // Helper function to check if RFQ has expired based on closing date
  const isRFQExpired = () => {
    if (!specification || !specification.procurement_closing) return false;
    
    try {
      // Parse the closing date (format: "15/09/2025")
      const [day, month, year] = specification.procurement_closing.split('/');
      const closingDate = new Date(year, month - 1, day); // month is 0-indexed
      
      // Add closing time if available (format: "22:20:48")
      if (specification.procurement_closing_time) {
        const [hours, minutes, seconds] = specification.procurement_closing_time.split(':');
        closingDate.setHours(parseInt(hours), parseInt(minutes), parseInt(seconds));
      } else {
        // Default to end of day if no time specified
        closingDate.setHours(23, 59, 59);
      }
      
      return new Date() > closingDate;
    } catch (error) {
      console.warn('Failed to parse RFQ closing date:', error);
      return false;
    }
  };

  // Helper function to check if user can view quotations
  const canViewQuotations = () => {
    // Match PHP logic: specification_user == user_id() || user_id() == 1 || department == department() || is_procurement()
    if (!user || !specification) return false;
    
    const isCreator = user && specification.specification_user === user.user_id;
    const isSameDepartment = user && specification.department === user.department;
    const hasPermission = isCreator || isSameDepartment;
    
    // For ESR (Emergency) specifications, return permission check only
    if (specification.specification_emergency === 1 || specification.specification_emergency === '1') {
      return hasPermission;
    }
    
    // For RFQ specifications, use enhanced logic matching RFQView
    // Quote content can be viewed if:
    // 1. RFQ status is 3 (manually unlocked) OR
    // 2. RFQ is closed OR
    // 3. RFQ has expired (closing date reached) OR
    // 4. All suppliers have submitted quotations
    const isRFQUnlocked = specification.procurement_status === 3 || specification.procurement_status === '3';
    const isRFQClosed = specification.procurement_closed === 1 || specification.procurement_closed === '1';
    const hasRFQExpired = isRFQExpired();
    
    // Check if all suppliers have submitted quotations
    // Use parsedSuppliers (selected suppliers) vs quotes (submissions received)
    const allSuppliersSubmitted = parsedSuppliers.length > 0 && 
                                 quotes.length > 0 && 
                                 parsedSuppliers.length === quotes.length;
    
    return hasPermission && (isRFQUnlocked || isRFQClosed || hasRFQExpired || allSuppliersSubmitted);
  };

  // Function to check if all quotes have been evaluated
  const areAllQuotesEvaluated = () => {
    if (!quotes || quotes.length === 0) {
      return false;
    }
    
    // Check if all submissions have evaluation data
    return quotes.every(quote => 
      quote.quotation_evaluation && 
      quote.quotation_evaluation.trim().length > 4
    );
  };

  // Function to check if requisition can be created
  const canCreateRequisition = () => {
    // No requisition exists yet and there are evaluated submissions
    return (
      !specification.requisition_id && 
      quotes && 
      quotes.length > 0 &&
      areAllQuotesEvaluated() &&
      canViewQuotations() // Must also have permission to view quotes
    );
  };

  // Function to check if quote values can be edited (ESR only, before full approval)
  const canEditQuoteValues = () => {
    // Must be an ESR specification
    const isESR = specification.specification_emergency === 1 || specification.specification_emergency === '1';
    if (!isESR) return false;
    
    // Check if user has permission to edit:
    // 1. Creator of the specification
    // 2. Same department as the specification
    // 3. Procurement role (role ID 4 or 6)
    const isCreator = user && specification.specification_user === user.user_id;
    const isSameDepartment = user && specification.specification_department === user.user_department;
    const isProcurement = user && (user.user_role === 4 || user.user_role === 6 || user.user_role === '4' || user.user_role === '6');
    
    if (!isCreator && !isSameDepartment && !isProcurement) return false;
    
    // Requisition must not be fully approved yet
    const requisitionStatus = parseInt(specification.requisition_status) || 0;
    const requiredApprovals = parseInt(specification.specification_approvals) || 0;
    
    // Allow editing if requisition status is less than required approvals
    // Also allow if there's no requisition yet (status would be 0 or undefined)
    return requisitionStatus < requiredApprovals;
  };

  // Handle quote evaluation
  const handleEvaluate = async (quoteId, evaluation) => {
    try {
      setIsEvaluating(true);
      console.log('💾 Saving evaluation for quote ID:', quoteId);
      
      // Find the original quote object with its original ID directly from our quotes array
      const quoteToEvaluate = quotes.find(q => {
        // Try exact match first (most reliable)
        if (q.quotation_id === quoteId) return true;
        
        // Fall back to string comparison if needed
        return ensureStringId(q.quotation_id) === ensureStringId(quoteId);
      });
      
      if (quoteToEvaluate) {
        console.log('✅ Found quote to evaluate with original API ID:', quoteToEvaluate.quotation_id);
        
        // Use the original ID from the API for the evaluation request
        const originalId = quoteToEvaluate.quotation_id;
        
        // Use the correct specifications evaluation endpoint with the original ID
        const response = await api.post('/api/specifications/evaluation', {
          quotation_id: originalId,
          values: evaluation
        });
        
        console.log('📊 Evaluation API response:', response.data);
        
        if (response.data.status === 1) {
          // Refresh data after evaluation is saved
          await fetchSpecification();
          
          toast.success('Evaluation saved successfully');
          return true; // Return success for QuoteViewModal callback
        } else {
          throw new Error(response.data.message || 'Failed to save evaluation');
        }
      } else {
        console.error('❌ Quote not found for evaluation - ID:', quoteId);
        console.error('   Available quotes:', quotes.map(q => q.quotation_id));
        throw new Error('Quote not found for evaluation. Please refresh and try again.');
      }
    } catch (err) {
      console.error('Evaluation error:', err);
      toast.error('Failed to save evaluation: ' + (err.response?.data?.message || err.message));
      return false; // Return failure for QuoteViewModal callback
    } finally {
      setIsEvaluating(false);
      setShowQuoteModal(false);
      setShowQuoteViewModal(false);
    }
  };

  // If loading, show spinner
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md shadow">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Specification</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard/specifications')}
                className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Return to Specifications
              </button>
              <button
                type="button"
                onClick={() => fetchSpecification()}
                className="ml-3 inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render the specification page
  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      {/* Specification Title and Reference Number */}
      <div className="border-b border-gray-200 pb-4 mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {specification.specification_title || 'Untitled Specification'}
        </h1>
        <div className="text-xl font-semibold text-blue-700">
          EPR-{specification.requisition_number || 'pending'}
        </div>
      </div>


              {/* Horizontal Timeline - Immediately visible for better UX */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-lg font-medium text-gray-900">
            {specification.specification_emergency === 1 ? 'ESR Process Timeline' : 'RFQ Process Timeline'}
          </h2>
        </div>
        <div className="py-4 px-2">
          <div className="relative">
            {/* Progress bar background */}
            <div className="absolute top-5 w-full h-1 bg-gray-200"></div>
            
            {/* Timeline steps */}
            <div className="relative flex justify-between items-start">
              {timeline.map((item, index) => {
                // Calculate if this step is active, completed, or upcoming
                const isCompleted = item.status === 'completed';
                const isActive = item.status === 'in_progress';
                const isLast = index === timeline.length - 1;
                
                return (
                  <div 
                    key={item.id} 
                    className={`flex-1 text-center ${isLast ? '' : 'relative'}`}
                  >
                    {/* Connector line - don't show for the last item */}
                    {!isLast && (
                      <div 
                        className={`absolute top-5 w-full h-1 ${isCompleted ? 'bg-blue-600' : 'bg-gray-200'}`}
                        style={{ left: '50%' }}
                      ></div>
                    )}
                    
                    {/* Step circle */}
                    <div className="inline-flex relative">
                      <div 
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-4 
                          ${isCompleted ? 'bg-blue-600 border-blue-100' : 
                            isActive ? 'bg-yellow-500 border-yellow-100' : 
                            'bg-gray-200 border-gray-100'}`}
                      >
                        {isCompleted ? (
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <span className="text-xs font-medium text-gray-600">{index + 1}</span>
                        )}
                      </div>
                      
                      {/* Action button */}
                      {item.action && (
                        <div className="absolute -right-3 -top-3">
                          <button
                            onClick={() => handleTimelineAction(item.action, item)}
                            className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Step label */}
                    <div className="mt-2 max-w-xs mx-auto">
                      <p className="text-xs font-medium text-gray-900">{item.title}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {item.date ? formatDate(item.date) : 'Pending'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {/* Key Action Buttons */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => navigate('/dashboard/specifications/esr')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Back to List
          </button>
        </div>
        
        <div className="flex space-x-2">
          {/* View Requisition button - only if requisition exists AND at least one quote has been evaluated */}
          {specification.requisition_number && specification.requisition_number !== '' && specification.requisition_number !== 'null' && 
            quotes && quotes.some(quote => quote.quotation_evaluation) && (
            <>
              {/* For ESR specifications: Show Select Quote and Declare if no declaration, or View Requisition if declared */}
              {(specification.specification_emergency === 1 || specification.specification_emergency === '1') ? (
                <Link
                  to={specification.requisition_declaration ? 
                    `/dashboard/requisitions/${specification.requisition_id || specification.requisition_number}` : 
                    `/dashboard/requisitions/edit/${specification.requisition_id || specification.requisition_number}`}
                  className={`inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    specification.requisition_declaration ? 
                    'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 
                    'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                >
                  <svg className="-ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                  {specification.requisition_declaration ? 'View Requisition' : 'Select Quote and Declare'}
                </Link>
              ) : (
                /* For RFQ specifications: Always show View Requisition once requisition exists */
                <Link
                  to={`/dashboard/requisitions/${specification.requisition_id || specification.requisition_number}`}
                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                  View Requisition
                </Link>
              )}
            </>
          )}

          {/* Create Requisition button - only if all quotes are evaluated and no requisition exists yet */}
          {canCreateRequisition() && (
            <Link
              to={`/dashboard/requisitions/create/${specification.specification_id}`}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Create Requisition
            </Link>
          )}
          
          {/* {originalUser && originalUser.user_id === 1 && (
            <button
              type="button"
              onClick={toggleDebugModal}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              Debug Info {!!originalUser ? "(Masquerading)" : ""}
            </button>
          )} */}
        </div>
      </div>

      {/* Key Information Card */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-lg font-medium text-gray-900">Specification Details</h2>
        </div>
        <div className="px-4 py-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Column 1 */}
            <div>
              <dl className="space-y-2">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Department</dt>
                  <dd className="mt-1 text-sm text-gray-900">{specification.department_name}</dd>
                </div>

                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Created By</dt>
                  <dd className="mt-1 text-sm text-gray-900">{specification.user_full_name}</dd>
                </div>
              </dl>
            </div>
            
            {/* Column 2 */}
            <div>
              <dl className="space-y-2">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Created Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(specification.specification_date)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Expected Delivery</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {specification.specification_delivery
                      ? formatDate(specification.specification_delivery) 
                      : 'Not specified'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">GL Description</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {specification.description || 'Not specified'}
                  </dd>
                </div>
              </dl>
            </div>
            
            {/* Column 3 */}
            <div>
              <dl className="space-y-2">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Status</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                      ${(() => {
                        if (specification.requisition_state == 1) return 'bg-gray-100 text-gray-800';
                        if (specification.requisition_status == 4) return 'bg-red-100 text-red-800';
                        
                        // Different logic for ESR vs RFQ specifications
                        const isESR = specification.specification_emergency === 1 || specification.specification_emergency === '1';
                        
                        if (isESR) {
                          // ESR uses approval-based status
                          const isApproved = specification.requisition_status >= (specification.specification_approvals || 0);
                          return isApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
                        } else {
                          // RFQ uses procurement status
                          if (specification.specification_status == 4) return 'bg-red-100 text-red-800';
                          if (specification.procurement_closed == 1) return 'bg-gray-100 text-gray-800';
                          if (specification.procurement_status == 1) return 'bg-blue-100 text-blue-800';
                          if (specification.procurement_status == 0) return 'bg-orange-100 text-orange-800';
                          return 'bg-yellow-100 text-yellow-800';
                        }
                      })()}`}>
                      {(() => {
                        if (specification.requisition_state == 1) return 'Junked';
                        if (specification.requisition_status == 4) return 'Rejected';
                        
                        // Different logic for ESR vs RFQ specifications
                        const isESR = specification.specification_emergency === 1 || specification.specification_emergency === '1';
                        
                        if (isESR) {
                          // ESR uses approval-based status
                          const isApproved = specification.requisition_status >= (specification.specification_approvals || 0);
                          return isApproved ? 'Approved' : 'Pending Approval';
                        } else {
                          // RFQ uses procurement status
                          if (specification.specification_status == 4) return 'Marked for Deletion';
                          if (specification.procurement_closed == 1) return 'Closed';
                          if (specification.procurement_status == 1) return 'Published';
                          if (specification.procurement_status == 0) return 'Draft';
                          return 'Pending';
                        }
                      })()}
                    </span>
                  </dd>
                </div>
                
                {/* Show deletion comment if specification is marked for deletion */}
                {specification.specification_status == 4 && specification.specification_comment && (
                  <div className="col-span-2 min-w-0">
                    <dt className="text-xs font-medium text-red-600 uppercase">Deletion Reason</dt>
                    <dd className="mt-1 text-sm text-gray-900 bg-red-50 p-3 rounded border border-red-200 break-words overflow-wrap-anywhere max-w-full" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      {specification.specification_comment}
                    </dd>
                  </div>
                )}
                
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Requisition Total</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-medium">
                    {(() => {
                      // For ESR specifications, calculate total from all quotes
                      if (specification.specification_emergency === 1 && quotes.length > 0) {
                        const totalQuotes = quotes.reduce((sum, quote) => {
                          if (!quote.quotation_total) return sum;
                          // Handle both string and number formats, remove commas and spaces
                          const cleanTotal = typeof quote.quotation_total === 'string' 
                            ? quote.quotation_total.replace(/[\s,]/g, '') 
                            : quote.quotation_total.toString();
                          const amount = parseFloat(cleanTotal) || 0;
                          return sum + amount;
                        }, 0);
                        return totalQuotes > 0 ? `R ${totalQuotes.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : 'Pending quotations';
                      }
                      
                      // For RFQ or fallback, use specification value
                      return parseFinancialValue(specification.specification_value) 
                        ? `R ${parseFinancialValue(specification.specification_value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` 
                        : 'Not specified';
                    })()}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Suppliers</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {parsedSuppliers.length} supplier{parsedSuppliers.length !== 1 ? 's' : ''} selected
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Column - Basic Info */}
        <div className="lg:col-span-2">
       
          {/* Quick Actions Card */}
          <div className="bg-white shadow rounded-lg mb-4">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-lg font-medium text-gray-900">Quick Actions</h2>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                {/* Dynamic Current Step Action */}
                {(() => {
                  const currentAction = getCurrentStepAction();
                  if (!currentAction) return null;
                  
                  return (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center">
                        <div className="flex-grow">
                          <h3 className="text-sm font-medium text-gray-900">{currentAction.title}</h3>
                          <p className="text-xs text-gray-600 mb-2">{currentAction.description}</p>
                          <button
                            onClick={currentAction.action}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            {currentAction.buttonText}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* ESR Suppliers with Quotation Status - New multi-quote approach */}
                {specification && (specification.specification_emergency === 1 || specification.specification_emergency === '1') && 
                 canViewQuotations() && parsedSuppliers.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">ESR Suppliers</h3>
                    {parsedSuppliers.map((supplier, index) => {
                      // Find quote for this specific supplier - prioritize company_code match
                      const supplierQuote = quotes.find(q => {
                        // Primary match by company code - ensure both are strings for comparison
                        if (supplier.company_code && q.quotation_company_code) {
                          const match = String(q.quotation_company_code) === String(supplier.company_code);
                          if (match) {
                            return true;
                          }
                        }
                        // Fallback to name match if codes aren't available
                        if (supplier.company_name && q.quotation_company_name) {
                          const match = q.quotation_company_name.toLowerCase().trim() === supplier.company_name.toLowerCase().trim();
                          if (match) {
                            return true;
                          }
                        }
                        return false;
                      });
                      
                      const isESR = specification.specification_emergency === 1 || specification.specification_emergency === '1';
                      const isCreator = user && specification.specification_user === user.user_id;
                      
                      return (
                        <div key={supplier.company_code || index} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <div className="flex items-center justify-between">
                            <div className="flex-grow">
                              <h4 className="text-sm font-medium text-gray-900">{supplier.company_name}</h4>
                              {supplierQuote && (
                                <p className="text-xs text-green-600 font-medium">
                                  Quote: R {(() => {
                                    if (!supplierQuote.quotation_total) return '0';
                                    // Handle both string and number formats, remove commas and spaces
                                    const cleanTotal = typeof supplierQuote.quotation_total === 'string' 
                                      ? supplierQuote.quotation_total.replace(/[\s,]/g, '') 
                                      : supplierQuote.quotation_total.toString();
                                    const amount = parseFloat(cleanTotal) || 0;
                                    return amount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
                                  })()}
                                </p>
                              )}
                            </div>
                            <div className="flex space-x-1">
                              {supplierQuote ? (
                                <>
                                  <button
                                    onClick={() => handleQuoteView(supplierQuote.quotation_company_code)}
                                    className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    View
                                  </button>
                                  {/* Show Edit button for ESR if requisition not fully approved */}
                                  {canEditQuoteValues() && (
                                    <button
                                      onClick={() => {
                                        // Set the supplier and existing quote for editing
                                        setSelectedSupplierForQuote(supplier);
                                        setExistingQuoteForEdit(supplierQuote);
                                        setShowAttachQuotationModal(true);
                                      }}
                                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                    >
                                      Edit
                                    </button>
                                  )}
                                  {/* Only show Evaluate button if the quote has not been evaluated yet */}
                                  {!supplierQuote.quotation_evaluation && (
                                    <button
                                      onClick={() => handleQuoteEvaluate(supplierQuote.quotation_id)}
                                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    >
                                      Evaluate
                                    </button>
                                  )}
                                </>
                              ) : (
                                // Only show Attach Quote button for ESR specifications created by current user
                                isESR && isCreator ? (
                                  <button
                                    onClick={() => {
                                      // Pre-select this supplier when opening attach modal
                                      setSelectedSupplierForQuote(supplier);
                                      setShowAttachQuotationModal(true);
                                    }}
                                    className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-orange-700 bg-orange-100 hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                  >
                                    Attach Quote
                                  </button>
                                ) : (
                                  // For RFQ types, show informational text instead of button
                                  !isESR && (
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500">
                                      Supplier Quote Only
                                    </span>
                                  )
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Create Requisition Action - when all quotes are evaluated but no requisition exists */}
                {canCreateRequisition() && (
                  <div className="p-3 bg-blue-50 border-blue-100 rounded-lg border">
                    <div className="flex items-center">
                      <div className="flex-grow">
                        <h3 className="text-sm font-medium text-gray-900">
                          <i className="fas fa-check-circle text-green-500 mr-2"></i>
                          Evaluation Completed
                        </h3>
                        <p className="text-xs text-gray-600 mb-2">All quotes have been evaluated. You can now create a requisition.</p>
                        <Link
                          to={`/dashboard/requisitions/create/${specification.specification_id}`}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Create Requisition
                        </Link>
                      </div>
                    </div>
                  </div>
                )}

                {/* View Requisition Action - Only show if at least one quote has been evaluated */}
                {specification.requisition_number && specification.requisition_number !== '' && specification.requisition_number !== 'null' && 
                 quotes && quotes.some(quote => quote.quotation_evaluation) && (
                  <div className={`p-3 ${specification.requisition_declaration ? 'bg-green-50 border-green-100' : 'bg-orange-50 border-orange-100'} rounded-lg border`}>
                    <div className="flex items-center">
                      <div className="flex-grow">
                        <h3 className="text-sm font-medium text-gray-900">
                          {specification.requisition_declaration ? 'View Requisition' : 'Select Quote and Declare'}
                        </h3>
                        <p className="text-xs text-gray-600 mb-2">
                          {specification.requisition_declaration ? 'Access requisition details and status' : 'Select a quote and complete declaration'}
                        </p>
                        <Link
                          to={specification.requisition_declaration ? 
                            `/dashboard/requisitions/${specification.requisition_id || specification.requisition_number}` : 
                            `/dashboard/requisitions/edit/${specification.requisition_id || specification.requisition_number}`}
                          className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md shadow-sm text-white ${
                            specification.requisition_declaration ? 
                            'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 
                            'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
                          } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                        >
                          {specification.requisition_declaration ? 'View' : 'Select & Declare'}
                        </Link>
                      </div>
                    </div>
                  </div>
                )}

                {/* View Single Quote Action - RFQ specifications only */}
                {canViewQuotations() && quotes.length === 1 && 
                 (specification.specification_emergency !== 1 && specification.specification_emergency !== '1') && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h3 className="text-sm font-medium text-gray-900">View Quotation</h3>
                    <p className="text-xs text-gray-600 mt-1">{quotes[0].quotation_company_name || 'Unknown Supplier'}</p>
                    <p className="text-xs text-gray-500 mt-1 mb-3">
                      {(() => {
                        if (!quotes[0].quotation_total) return 'No value';
                        // Handle both string and number formats, remove commas and spaces
                        const cleanTotal = typeof quotes[0].quotation_total === 'string' 
                          ? quotes[0].quotation_total.replace(/[\s,]/g, '') 
                          : quotes[0].quotation_total.toString();
                        const amount = parseFloat(cleanTotal) || 0;
                        return amount > 0 ? `R ${amount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : 'No value';
                      })()}
                    </p>
                    <div className="flex space-x-1">
                      <Button 
                        onClick={() => handleQuoteView(quotes[0].quotation_company_code)}
                        variant="primary"
                        className="text-[10px] px-1 py-0.5 flex-1"
                      >
                        View
                      </Button>
                      {/* Only show Evaluate button if the quote has not been evaluated yet */}
                      {canViewQuotations() && !quotes[0].quotation_evaluation && (
                        <Button 
                          onClick={() => handleQuoteEvaluate(quotes[0].quotation_id)}
                          variant="success"
                          className="text-[10px] px-1 py-0.5 flex-1"
                        >
                          Evaluate
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Compare Multiple Quotes Action - RFQ specifications only */}
                {canViewQuotations() && quotes.length > 1 && 
                 (specification.specification_emergency !== 1 && specification.specification_emergency !== '1') && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                    <h3 className="text-sm font-medium text-gray-900">Compare Quotes</h3>
                    <p className="text-xs text-gray-600 mt-1 mb-3">Review {quotes.length} quotes received</p>
                    <div className="space-y-3">
                      {quotes.map((quote, index) => (
                        <div
                          key={quote.quotation_company_code}
                          className="p-3 bg-white rounded border border-purple-100"
                        >
                          <div className="flex items-center mb-2">
                            <div className="w-6 h-6 flex items-center justify-center bg-purple-600 text-white rounded-full text-xs font-medium mr-2">
                              {index + 1}
                            </div>
                            <div className="flex-grow">
                              <p className="text-xs font-medium text-gray-900">
                                {quote.quotation_company_name || 'Unknown Supplier'}
                              </p>
                              <p className="text-xs text-gray-900 font-medium">
                                {(() => {
                                  if (!quote.quotation_total) return 'No value';
                                  // Handle both string and number formats, remove commas and spaces
                                  const cleanTotal = typeof quote.quotation_total === 'string' 
                                    ? quote.quotation_total.replace(/[\s,]/g, '') 
                                    : quote.quotation_total.toString();
                                  const total = parseFloat(cleanTotal) || 0;
                                  
                                  if (total === 0) return 'No value';
                                  
                                  // Check if VAT exists and calculate subtotal
                                  const cleanVat = typeof quote.quotation_vat === 'string'
                                    ? quote.quotation_vat.replace(/[\s,]/g, '')
                                    : (quote.quotation_vat || '0').toString();
                                  const vat = parseFloat(cleanVat) || 0;
                                  const subtotal = total - vat;
                                  
                                  const subtotalStr = `R ${subtotal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                                  
                                  if (vat > 0) {
                                    const vatStr = `(VAT: R ${vat.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})})`;
                                    return `${subtotalStr} ${vatStr}`;
                                  }
                                  
                                  return subtotalStr;
                                })()}
                              </p>
                            </div>
                          </div>
                          <div className="flex space-x-1">
                            <Button
                              onClick={() => handleQuoteView(quote.quotation_company_code)}
                              variant="primary"
                              className="text-[10px] px-1 py-0.5 flex-1"
                            >
                              View
                            </Button>
                            {/* Only show Evaluate button if the quote has not been evaluated yet */}
                            {!quote.quotation_evaluation && (
                              <Button
                                onClick={() => handleQuoteEvaluate(quote.quotation_id)}
                                variant="success"
                                className="text-[10px] px-1 py-0.5 flex-1"
                              >
                                Evaluate
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No suppliers selected but has submissions - Match PHP edge case */}
                {canViewQuotations() && (!parsedSuppliers || parsedSuppliers.length === 0) && quotes.length > 0 && (
                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                    <h3 className="text-sm font-medium text-gray-900">No Supplier Selected</h3>
                    <p className="text-xs text-gray-600 mt-1 mb-3">Quotation available but no supplier selected</p>
                    <div className="flex space-x-1">
                      <Button
                        to={`/dashboard/specifications/edit/${specification.specification_id}`}
                        variant="warning"
                        className="text-[10px] px-1 py-0.5 flex-1"
                      >
                        Select Supplier
                      </Button>
                      <Button 
                        onClick={() => handleQuoteView(quotes[0].quotation_company_code)}
                        variant="primary"
                        className="text-[10px] px-1 py-0.5 flex-1"
                      >
                        View Quotation
                      </Button>
                    </div>
                  </div>
                )}

                {/* Mark Ready for Sending Action */}
                {(() => {
                  // Find the mark_ready_for_sending action from timeline
                  const readyForSendingStep = timeline.find(item => item.action === 'mark_ready_for_sending');
                  
                  if (!readyForSendingStep) return null;
                  
                  return (
                    <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                      <div className="flex items-center">
                        <div className="flex-grow">
                          <h3 className="text-sm font-medium text-gray-900">Mark Ready for Sending</h3>
                          <p className="text-xs text-gray-600 mb-2">Mark this specification as ready to be sent to suppliers</p>
                          <button
                            onClick={() => handleTimelineAction('mark_ready_for_sending', readyForSendingStep)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                          >
                            Mark Ready for Sending
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Mark for Deletion Action - Only for draft RFQ specifications */}
                {specification.specification_status === 0 && 
                 specification.specification_emergency === 0 &&
                 (user.user_id === specification.specification_user || 
                  user.user_department === specification.specification_department) && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex items-center">
                      <div className="flex-grow">
                        <h3 className="text-sm font-medium text-gray-900">Mark for Deletion</h3>
                        <p className="text-xs text-gray-600 mb-2">Mark this draft RFQ specification for deletion</p>
                        <button
                          onClick={handleMarkForDeletion}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Mark for Deletion
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div> 
          </div> 
         
         

          {/* Documents & Attachments - moved from right column */}
          <div className="bg-white shadow rounded-lg mb-4">
            <div className="border-b border-gray-200 px-4 py-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">
                  Documents & Attachments
                </h2>
                <button
                  onClick={() => setShowFilesModal(true)}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  Manage Files
                </button>
              </div>
            </div>
            <div className="p-4">
              {attachments && attachments.length > 0 ? (
                <div className="space-y-2">
                  {attachments.slice(0, 3).map((attachment, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center min-w-0 flex-1">
                        <svg className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span 
                          className="text-sm text-gray-900 break-words"
                          style={{ 
                            wordBreak: 'break-word',
                            lineHeight: '1.4',
                            maxWidth: '100%'
                          }}
                          title={attachment.file_name || attachment.name || `File ${index + 1}`}
                        >
                          {attachment.file_name || attachment.name || `File ${index + 1}`}
                        </span>
                      </div>
                    </div>
                  ))}
                  {attachments.length > 3 && (
                    <div className="text-sm text-gray-500 text-center mt-2">
                      And {attachments.length - 3} more file{attachments.length - 3 !== 1 ? 's' : ''}...
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-gray-500 text-sm">No documents attached</p>
                  <p className="text-gray-400 text-xs mt-1">Click "Manage Files" to upload specification documents</p>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Right Column - Content */}
        <div className="lg:col-span-3 space-y-4">
          {/* Specification text */}
          <div className="bg-white shadow rounded-lg mb-4">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-lg font-medium text-gray-900">
                Specification Items
              </h2>
            </div>
            <div className="p-4">
              {/* Items Table - Displays the key specification items in a structured format */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Parse procurement_items and procurement_qty from PHP serialized data */}
                    {(() => {
                      try {
                        // Parse procurement items if available
                        let items = [];
                        let quantities = [];
                        
                        if (specification.procurement_items) {
                          // Simple parsing of PHP serialized array format a:2:{i:0;s:2:"i1";i:1;s:2:"i2";}
                          const itemMatches = specification.procurement_items.match(/s:\d+:"([^"]*)"/g);
                          if (itemMatches) {
                            items = itemMatches.map(m => m.replace(/s:\d+:"([^"]*)"/, '$1'));
                          }
                        }
                        
                        if (specification.procurement_qty) {
                          // Simple parsing of PHP serialized array format
                          const qtyMatches = specification.procurement_qty.match(/s:\d+:"([^"]*)"/g);
                          if (qtyMatches) {
                            quantities = qtyMatches.map(m => m.replace(/s:\d+:"([^"]*)"/, '$1'));
                          }
                        }
                        
                        if (items.length > 0) {
                          return items.map((item, index) => (
                            <tr key={index}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{item}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {quantities[index] || '1'}
                              </td>
                            </tr>
                          ));
                        } else if (specification.specification_items) {
                          // Alternative item source
                          return (
                            <tr>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{specification.specification_items}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                {specification.specification_qty || '1'}
                              </td>
                            </tr>
                          );
                        } else {
                          return (
                            <tr>
                              <td colSpan="2" className="px-3 py-2 text-sm text-center text-gray-500">
                                <div className="py-8">
                                  <p>No specification items defined.</p>
                                  <p className="mt-2 font-medium">See specification text for details.</p>
                                </div>
                              </td>
                            </tr>
                          );
                        }
                      } catch (error) {
                        console.error("Error parsing specification items:", error);
                        return (
                          <tr>
                            <td colSpan="2" className="px-3 py-2 text-sm text-center text-gray-500">
                              <div className="py-8">
                                <p>Error parsing specification items.</p>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                    })()}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="1" className="px-3 py-2 text-sm font-medium text-right text-gray-700">
                        {quotes.length > 0 ? 'Lowest Quote:' : 'Quotation Total:'}
                      </td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">
                        {(() => {
                          if (quotes.length > 0) {
                            // Find the lowest quote value
                            const validQuotes = quotes
                              .map(quote => {
                                if (!quote.quotation_total) return 0;
                                const cleanTotal = typeof quote.quotation_total === 'string' 
                                  ? quote.quotation_total.replace(/[\s,]/g, '') 
                                  : quote.quotation_total.toString();
                                return parseFloat(cleanTotal) || 0;
                              })
                              .filter(amount => amount > 0);
                            
                            if (validQuotes.length > 0) {
                              const lowestQuote = Math.min(...validQuotes);
                              return `R ${lowestQuote.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} (${quotes.length} quote${quotes.length > 1 ? 's' : ''} received)`;
                            }
                            return 'No values submitted';
                          }
                          return 'Pending quotations';
                        })()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              
              {/* Expected Delivery Date */}
              {specification.specification_delivery && (
                <div className="mt-4 flex justify-end">
                  <div className="inline-flex items-center px-3 py-2 rounded-md bg-blue-50 border border-blue-100">
                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-800">
                      Expected Delivery: {formatDate(specification.specification_delivery)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Specification Details - moved from left column - only show if content exists */}
          {specification.specification_spec && specification.specification_spec.trim() && (
            <div className="bg-white shadow rounded-lg mb-4">
              <div className="border-b border-gray-200 px-4 py-3">
                <h2 className="text-lg font-medium text-gray-900">
                  Specification Details
                </h2>
              </div>
              <div className="p-4">
                <div className="prose max-w-none">
                  <div 
                    dangerouslySetInnerHTML={{
                      __html: specification.specification_spec
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          

          {/* Quotations Card - Match PHP logic for showing quotations */}
          {canViewQuotations() && quotes.length > 0 && (
            <div className="bg-white shadow rounded-lg mb-4">
              <div className="border-b border-gray-200 px-4 py-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">
                    Quotations ({quotes.length})
                  </h2>
                  <div className="text-sm text-gray-600">
                    {(() => {
                      const validQuotes = quotes.filter(q => q.quotation_total);
                      if (validQuotes.length > 0) {
                        const amounts = validQuotes.map(q => {
                          // Handle both string and number formats, remove commas and spaces
                          const cleanTotal = typeof q.quotation_total === 'string' 
                            ? q.quotation_total.replace(/[\s,]/g, '') 
                            : q.quotation_total.toString();
                          return parseFloat(cleanTotal) || 0;
                        });
                        const min = Math.min(...amounts);
                        const max = Math.max(...amounts);
                        return `Range: R ${min.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} - R ${max.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                      }
                      return 'No values submitted';
                    })()}
                  </div>
                </div>
              </div>
              <div className="p-4">
                <div className="space-y-2">
                  {quotes.map((quote, index) => (
                    <div 
                      key={quote.quotation_id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleQuoteView(quote.quotation_company_code)}
                    >
                      <div className="flex items-center">
                        <div className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-full mr-3 text-sm font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {quote.quotation_company_name || 'Unknown Supplier'}
                          </p>
                          <div className="flex items-center space-x-3 mt-1">
                            <div className="flex items-center space-x-4 min-w-[280px]">
                              <p className="text-sm font-bold text-blue-600">
                                {(() => {
                                  if (!quote.quotation_total) return 'No value';
                                  // Handle both string and number formats, remove commas and spaces
                                  const cleanTotal = typeof quote.quotation_total === 'string' 
                                    ? quote.quotation_total.replace(/[\s,]/g, '') 
                                    : quote.quotation_total.toString();
                                  const total = parseFloat(cleanTotal) || 0;
                                  
                                  if (total === 0) return 'No value';
                                  
                                  // Calculate subtotal (total minus VAT)
                                  const cleanVat = typeof quote.quotation_vat === 'string'
                                    ? quote.quotation_vat.replace(/[\s,]/g, '')
                                    : (quote.quotation_vat || '0').toString();
                                  const vat = parseFloat(cleanVat) || 0;
                                  const subtotal = total - vat;
                                  
                                  return `R ${subtotal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                                })()}
                              </p>
                              {(() => {
                                if (!quote.quotation_total) return null;
                                // Only show VAT if it's greater than 0
                                const cleanVat = typeof quote.quotation_vat === 'string'
                                  ? quote.quotation_vat.replace(/[\s,]/g, '')
                                  : (quote.quotation_vat || '0').toString();
                                const vat = parseFloat(cleanVat) || 0;
                                if (vat > 0) {
                                  return (
                                    <p className="text-xs text-gray-500 whitespace-nowrap">
                                      (VAT: R {vat.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})})
                                    </p>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            <p className="text-xs text-gray-500">
                              Code: {quote.quotation_company_code}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(quote.quotation_time * 1000).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {quote.quotation_evaluation ? (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                            ✓ Evaluated
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                            Pending
                          </span>
                        )}
                        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quote Modal */}
      {selectedQuote && (
        <QuoteModal
          isOpen={showQuoteModal}
          onClose={() => setShowQuoteModal(false)}
          quote={selectedQuote}
          specification={specification}
          supplier={parsedSuppliers.find(s => s.company_code === selectedQuote.quotation_company_code)}
          onEvaluate={handleEvaluate}
          isEvaluating={isEvaluating}
          canEvaluate={canViewQuotations()}
        />
      )}

      {/* Quote View Modal for Evaluation */}
      {selectedQuote && (
        <QuoteViewModal
          isOpen={showQuoteViewModal}
          onClose={() => setShowQuoteViewModal(false)}
          quote={selectedQuote}
          canMakeCorrections={canViewQuotations()}
          specificationId={specificationId}
          onEvaluationSave={handleEvaluate}
        />
      )}

      {/* Attach Quotation Modal */}
      {specification && (
        <AttachQuotationModal
          isOpen={showAttachQuotationModal}
          onClose={() => {
            setShowAttachQuotationModal(false);
            setSelectedSupplierForQuote(null);
            setExistingQuoteForEdit(null);
          }}
          specificationId={specificationId}
          suppliers={parsedSuppliers}
          specification={specification}
          selectedSupplier={selectedSupplierForQuote}
          existingQuote={existingQuoteForEdit}
          onSuccess={() => {
            // Add a longer delay to ensure database commit is complete
            setTimeout(async () => {
              console.log('🔄 Refreshing specification data after quotation submission...');
              await fetchSpecification();
              setShowAttachQuotationModal(false);
              setSelectedSupplierForQuote(null);
              setExistingQuoteForEdit(null);
            }, 1000);
          }}
        />
      )}

      {/* Specification Files Modal */}
      <SpecificationFilesModal
        show={showFilesModal}
        onHide={() => setShowFilesModal(false)}
        files={attachments}
        specificationId={specificationId}
        onFilesUpdated={handleFilesUpdated}
      />

      {/* Debug Modal */}
      {originalUser && originalUser.user_id === 1 && (
        <DebugModal
          isOpen={showDebugModal}
          onClose={() => setShowDebugModal(false)}
          data={{
            user,
            specification,
            details,
            quotes,
            timeline,
            parsedSuppliers,
            requisitions: null
          }}
        />
      )}
    </div>
  );
}

// Enhanced Debug Modal Component for Specifications
const DebugModal = ({ isOpen, onClose, data }) => {
  const { user: authUser } = useAuth();
  const [selectedSection, setSelectedSection] = useState('overview');
  
  if (!data || !isOpen) return null;

  const dataStructure = {
    overview: {
      title: 'Overview',
      icon: '📋',
      data: {
        specification_id: data.specification?.specification_id,
        specification_title: data.specification?.specification_title,
        specification_emergency: data.specification?.specification_emergency,
        specification_status: data.specification?.specification_status,
        specification_approvals: data.specification?.specification_approvals,

        department_name: data.specification?.department_name,
        user_full_name: data.specification?.user_full_name,
        specification_date: data.specification?.specification_date,
        requisition_number: data.specification?.requisition_number,
        requisition_status: data.specification?.requisition_status
      }
    },
    workflow: {
      title: 'Workflow & Timeline',
      icon: '⚡',
      data: {
        timeline: data.timeline || [],
        specification_emergency: data.specification?.specification_emergency,
        procurement_id: data.specification?.procurement_id,
        procurement_time: data.specification?.procurement_time,
        requisition_id: data.specification?.requisition_id,
        requisition_date: data.specification?.requisition_date,
        purchase_order_number: data.specification?.purchase_order_number,
        purchase_order_date: data.specification?.purchase_order_date
      }
    },
    suppliers: {
      title: 'Suppliers & RFQ',
      icon: '🏢',
      data: {
        parsedSuppliers: data.parsedSuppliers || [],
        raw_supplier_data: data.specification?.specification_supplier,
        procurement_ref: data.specification?.procurement_ref,
        rfq_status: data.specification?.procurement_status,
        rfq_sent_count: data.quotes?.length || 0
      }
    },
    quotations: {
      title: 'Quotations & Submissions',
      icon: '💰',
      data: {
        quotes_count: data.quotes?.length || 0,
        quotes: data.quotes || [],
        details: data.details || {},
        selected_quotation: data.specification?.requisition_quote
      }
    },
    items: {
      title: 'Items & Specifications',
      icon: '📦',
      data: {
        specification_spec: data.specification?.specification_spec,
        specification_items: data.specification?.specification_items,
        specification_qty: data.specification?.specification_qty,
        procurement_items: data.specification?.procurement_items,
        procurement_qty: data.specification?.procurement_qty,
        specification_delivery: data.specification?.specification_delivery,
        specification_value: data.specification?.specification_value
      }
    },
    users: {
      title: 'Users & Permissions',
      icon: '👥',
      data: {
        current_user: authUser || {},
        specification_user: data.specification?.specification_user,
        user_full_name: data.specification?.user_full_name,
        user_email: data.specification?.user_email,
        department: data.specification?.department,
        role: data.specification?.role,
        user_permissions: data.user?.permissions || []
      }
    },
    raw: {
      title: 'Raw Data',
      icon: '🔧',
      data: data
    }
  };

  return (
    <div className={`fixed inset-0 z-50 overflow-hidden ${isOpen ? 'block' : 'hidden'}`}>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className="relative w-full h-full flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-5/6 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-blue-600 text-white rounded-t-lg">
            <h2 className="text-lg font-semibold">
              <svg className="w-5 h-5 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Debug Information - {data.specification?.specification_title || 'Specification'}
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 p-1 rounded"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="flex flex-1 overflow-hidden">
            {/* Navigation Sidebar */}
            <div className="w-1/4 border-r bg-gray-50 overflow-y-auto">
              <div className="p-4">
                <h6 className="text-sm font-bold mb-4 uppercase tracking-wider text-gray-600">
                  Data Sections
                </h6>
                <div className="space-y-1">
                  {Object.entries(dataStructure).map(([key, section]) => (
                    <button
                      key={key}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 text-sm font-medium ${
                        selectedSection === key 
                          ? 'bg-blue-600 text-white shadow-sm' 
                          : 'text-gray-700 hover:bg-blue-50 border border-gray-200'
                      }`}
                      onClick={() => setSelectedSection(key)}
                    >
                      <span className="mr-2">{section.icon}</span>
                      {section.title}
                      {key === 'overview' && data.specification?.specification_id && (
                        <div className="text-xs mt-1 opacity-75">
                          ID: {data.specification.specification_id}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Content Area */}
            <div className="flex-1 overflow-hidden">
              <div className="p-4 h-full overflow-auto">
                <div className="flex items-center mb-4">
                  <span className="mr-3 text-xl">{dataStructure[selectedSection].icon}</span>
                  <h5 className="text-lg font-semibold text-gray-900">
                    {dataStructure[selectedSection].title}
                  </h5>
                </div>
                
                {selectedSection === 'raw' ? (
                  <div className="w-full">
                    <pre className="p-4 rounded-lg text-xs overflow-auto whitespace-pre-wrap break-words bg-gray-900 text-green-400 max-h-96 border border-gray-300">
                      {JSON.stringify(dataStructure[selectedSection].data, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <div className="structured-data">
                    {selectedSection === 'overview' && data.specification && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="rounded-lg border border-blue-200 shadow-sm">
                          <div className="px-4 py-2 bg-blue-600 text-white rounded-t-lg">
                            <h6 className="text-sm font-semibold">Specification Details</h6>
                          </div>
                          <div className="p-4 bg-white">
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="font-medium">ID:</span>
                                <span>{data.specification.specification_id}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">Type:</span>
                                <span className={`px-2 py-1 rounded text-xs ${
                                  data.specification.specification_emergency === 1 
                                    ? 'bg-red-100 text-red-800' 
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {data.specification.specification_emergency === 1 ? 'ESR' : 'RFQ'}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="font-medium">Status:</span>
                                <span className={`px-2 py-1 rounded text-xs ${
                                  data.specification.specification_status === 4
                                    ? 'bg-red-100 text-red-800'
                                    : data.specification.specification_status === 1 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {data.specification.specification_status === 4 
                                    ? 'Marked for Deletion' 
                                    : data.specification.specification_status === 1 
                                      ? 'Approved' 
                                      : 'Pending'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">Date:</span>
                                <span>{data.specification.specification_date}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="rounded-lg border border-green-200 shadow-sm">
                          <div className="px-4 py-2 bg-green-600 text-white rounded-t-lg">
                            <h6 className="text-sm font-semibold">Progress Info</h6>
                          </div>
                          <div className="p-4 bg-white">
                            <div className="space-y-2 text-sm">

                              <div className="flex justify-between">
                                <span className="font-medium">Department:</span>
                                <span className="text-right ml-2">{data.specification.department_name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">Requester:</span>
                                <span>{data.specification.user_full_name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">Requisition:</span>
                                <span>{data.specification.requisition_number || 'Pending'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="w-full bg-gray-50 rounded-lg border">
                      <pre className="p-4 text-xs overflow-auto whitespace-pre-wrap break-words max-h-96 text-gray-800">
                        {JSON.stringify(dataStructure[selectedSection].data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center p-4 border-t bg-gray-50 rounded-b-lg">
            <small className="text-gray-600">
              Current Section: <strong>{dataStructure[selectedSection].title}</strong>
            </small>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Close Debug
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewSpecification;
